import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useSelector } from 'react-redux';
import { get, isNil } from 'lodash';
import PropTypes from 'prop-types';

import { checkinAction, nextAction, selectOfferAction } from 'actions/workflow';
import useWorkflowGuard from 'hooks/useWorkflowGuard';
import { FormColumn, FormRow, Input } from 'components/Form';
import Validator from 'components/Validator';
import { Button } from 'components/Button';
import Spinner from 'components/Spinner';
import GeneralError from 'components/GeneralError';
import Alert from 'components/Alert';
import Modal from 'components/Modal';
import InfoTooltip from 'components/InfoTooltip';
import { formatCurrency, formatPercentage } from 'utils/format';
import { REPAYMENT_TYPE_OPTIONS } from 'utils/constants';
import { currencyMask, unmask } from 'utils/masks';
import { getCryptoIcon } from 'utils/helper';
import CongratulationsImg from 'assets/images/congratulations.png';

import RegradeSchema from './schema';

const SelectOffer = ({
  navigate,
  searchParams,
  dispatch,
  validator: { errors, values, validate, onChangeHandler }
}) => {
  useWorkflowGuard(searchParams, navigate);
  const workflow = useSelector((state) => get(state, 'workflow'));
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegrading, setIsRegrading] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const [regradeErrorMessage, setRegradeErrorMessage] = useState(null);
  const [isAdjustExpanded, setIsAdjustExpanded] = useState(false);
  const [isCongratulationsModalShown, setIsCongratulationsModalShown] = useState(true);
  const [otherOffers, setOtherOffers] = useState([]);
  const [offerBeingSelected, setOfferBeingSelected] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const showAPRdisclaimer = useMemo(() => get(workflow, 'data.hasCompletedPlaid') && get(selectedOffer, 'repaymentType') === 'Interest Only', [selectedOffer, workflow]);
  const isCounterOffer = selectedOffer && get(workflow, 'data.requestedAmount') && get(selectedOffer, 'loanAmount') < get(workflow, 'data.requestedAmount');
  const totalOfferCount = (get(workflow, 'data.offers') || []).length;

  useEffect(() => {
    const offers = get(workflow, 'data.offers') || [];
    const hasCompletedPlaid = get(workflow, 'data.hasCompletedPlaid');
    const preferredLTV = get(workflow, 'data.preferredLtv');
    const currentRepaymentType = get(workflow, 'data.repaymentType');
    const requestedAmount = get(workflow, 'data.requestedAmount');

    if (currentRepaymentType) setActiveTab((prev) => prev ?? currentRepaymentType);

    // Filter offers based on autopay/plaid status for Interest Only
    let availableOffers = offers.filter((offer) => {
      if (offer.repaymentType === 'Interest Only') {
        return hasCompletedPlaid ? offer.isAutoPay : !offer.isAutoPay;
      }
      return true;
    });

    // Happy path: exact match on requested amount + repayment type + LTV
    const exactMatch = availableOffers.find(
      (offer) =>
        offer.loanAmount === requestedAmount &&
        offer.repaymentType === currentRepaymentType &&
        offer.ltv === preferredLTV
    );

    if (exactMatch) {
      setSelectedOffer(exactMatch);
    } else {
      const sorted = [...availableOffers].sort((a, b) => {
        // Priority 1: Highest loan amount
        const amountDiff = (b.loanAmount || 0) - (a.loanAmount || 0);
        if (amountDiff !== 0) return amountDiff;

        // Priority 2: Matching repayment type preferred
        const aMatchesType = a.repaymentType === currentRepaymentType ? 1 : 0;
        const bMatchesType = b.repaymentType === currentRepaymentType ? 1 : 0;
        if (bMatchesType !== aMatchesType) return bMatchesType - aMatchesType;

        // Priority 3: Closest LTV to preferred
        const aLtvDiff = Math.abs((a.ltv || 0) - (preferredLTV || 0));
        const bLtvDiff = Math.abs((b.ltv || 0) - (preferredLTV || 0));
        return aLtvDiff - bLtvDiff;
      });

      setSelectedOffer(get(sorted, '0') || null);
    }
  }, [workflow]);

  const getOtherOffersForType = (repaymentType) => {
    const offers = get(workflow, 'data.offers') || [];
    const hasCompletedPlaid = get(workflow, 'data.hasCompletedPlaid');

    let tabOffers = offers.filter((offer) => offer.repaymentType === repaymentType);
    if (repaymentType === 'Interest Only') {
      tabOffers = tabOffers.filter((o) => (hasCompletedPlaid ? o.isAutoPay : !o.isAutoPay));
    }

    return tabOffers.filter((offer) => offer !== selectedOffer);
  };

  const tabsWithOtherOffers = REPAYMENT_TYPE_OPTIONS.map((tab) => {
    const repaymentType = tab.apiLabel ?? tab.label;
    return { tab, repaymentType, hasOffers: getOtherOffersForType(repaymentType).length > 0 };
  });

  const hasAnyOtherOffers = tabsWithOtherOffers.some((t) => t.hasOffers);

  useEffect(() => {
    setOtherOffers(activeTab ? getOtherOffersForType(activeTab) : []);
  }, [workflow, activeTab, selectedOffer]);

  useEffect(() => {
    if (!activeTab) return;

    const activeHasOffers = tabsWithOtherOffers.some((t) => t.repaymentType === activeTab && t.hasOffers);
    if (!activeHasOffers) {
      const firstAvailable = tabsWithOtherOffers.find((t) => t.hasOffers);
      if (firstAvailable) setActiveTab(firstAvailable.repaymentType);
    }
  }, [workflow, activeTab, selectedOffer]);

  useEffect(() => {
    setTimeout(() => {
      setIsCongratulationsModalShown(false);
    }, 5000);
  }, []);

  useEffect(() => {
    const aprDisclaimer = document.querySelector('.apr-autopay-disclaimer');
    if (!aprDisclaimer) return;

    if (showAPRdisclaimer) {
      aprDisclaimer.classList.remove('hidden');
    } else {
      aprDisclaimer.classList.add('hidden');
    }
  }, [showAPRdisclaimer]);

  const handleChange = (field, value) => {
    onChangeHandler(field, value);
  };

  const handleNext = (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    setGeneralError(null);

    dispatch(nextAction({
      key: searchParams.get('key'),
      data: {
        subEntityType: get(selectedOffer, 'subEntityType'),
        subEntityId: get(selectedOffer, 'subEntityId'),
        offerName: get(selectedOffer, 'offerName'),
        isAutoPay: get(selectedOffer, 'isAutoPay'),
      },
      success: (res) => {
        navigate(get(res, 'data.url'));
        setIsSubmitting(false);
      },
      fail: (err) => {
        setIsSubmitting(false);
        setGeneralError(get(err, 'data.failure.errors.0') || { message: 'Something went wrong' });
      }
    }));
  };

  const handleOfferSelection = (e, offer, idx) => {
    e.preventDefault();
    setOfferBeingSelected(idx);
    
    dispatch(selectOfferAction({
      key: searchParams.get('key'),
      data: {
        selectPreferred: true,
        repaymentType: get(offer, 'repaymentType') ? get(REPAYMENT_TYPE_OPTIONS.find(o => (o.apiLabel ?? o.label) === get(offer, 'repaymentType')), 'value') : undefined,
        preferredLtv: get(offer, 'ltv'),
      },
      success: () => {
        window.scrollTo(0, 0);
        setOfferBeingSelected(null);
        setSelectedOffer(offer);
      },
      fail: (err) => {
        setGeneralError(get(err, 'data.failure.errors.0') || { message: 'Something went wrong' });
        setOfferBeingSelected(null);
      }
    }));
  };

  const handleAdjustSectionClick = (e) => {
    e.preventDefault();
    setIsAdjustExpanded(!isAdjustExpanded);
  };

  const handleRegrade = (e) => {
    e.preventDefault();

    if (validate().isValid) {
      setIsRegrading(true);
      setRegradeErrorMessage(null);

      dispatch(checkinAction({
        key: searchParams.get('key'),
        data: {
          regrade: true,
          requestedAmount: Number(unmask(values.requestedAmount)),
        },
        success: () => {
          window.scrollTo(0, 0);
          setIsRegrading(false);
        },
        fail: (err) => {
          setIsRegrading(false);
          setRegradeErrorMessage(get(err, 'data.failure.errors.0.message') || 'Something went wrong');
        }
      }));
    }
  };

  const toggleCongratulationsModal = () => {
    setIsCongratulationsModalShown(!isCongratulationsModalShown);
  };

  return (
    <div className="flex flex-col items-center mb-8 px-0 md:px-4">
      <div className="bg-sky-100 p-4 pt-6 md:pt-8 md:p-8 rounded mb-6 md:pb-10 w-full">
        <div className="flex items-center justify-center mb-4 gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <span className="text-green-500 uppercase font-semibold">Your Selected Offer</span>
        </div>
        <div className="flex flex-col gap-2 justify-center items-center mb-4">
          <span className="text-4xl md:text-5xl font-semibold text-blue-500">${formatCurrency(get(selectedOffer, 'loanAmount'))}</span>
          <span className="uppercase font-medium text-sm text-slate-500">Loan Amount*</span>
        </div>

        <div className="flex justify-center items-center mb-6">
          <span className="font-medium border border-blue-500 text-blue-500 text-sm px-3 py-1 rounded-lg">{get(selectedOffer, 'repaymentType') === 'No Payment' ? 'No Monthly Payment' : get(selectedOffer, 'repaymentType')}</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-[1px] bg-blue-200">
          <div className="px-2 py-4 md:py-5 md:px-5 flex flex-col justify-center md:justify-start items-center md:items-start bg-sky-100">
            <span className="text-lg md:text-2xl font-semibold">{!isNil(get(selectedOffer, 'apr')) ? `${formatPercentage(get(selectedOffer, 'ltv'))}%` : '-'}</span>
            <span className="text-center md:text-left uppercase text-xs text-slate-500">ltv**</span>
          </div>
          <div className="px-2 py-4 md:py-5 md:px-5  flex flex-col justify-center md:justify-start items-center md:items-start bg-sky-100">
            <span className="text-lg md:text-2xl font-semibold flex items-center gap-2">
              {!isNil(get(selectedOffer, 'collateralRequired'))
                ? `${get(selectedOffer, 'collateralRequired')} ${get(selectedOffer, 'coin') || ''}`
                : '-'}
                {get(selectedOffer, 'coin') ? getCryptoIcon(get(selectedOffer, 'coin').toLowerCase(), 24) : null}
              </span>
            <span className="text-center md:text-left uppercase text-xs text-slate-500">est. collateral</span>
          </div>
          <div className="px-2 py-4 md:py-5 md:px-5  flex flex-col justify-center md:justify-start items-center md:items-start bg-sky-100">
            <span className="text-xl md:text-2xl font-semibold">{!isNil(get(selectedOffer, 'apr')) ? `${formatPercentage(get(selectedOffer, 'apr'))}%` : '-'}</span>
            <span className="text-center md:text-left uppercase text-xs text-slate-500">apr{showAPRdisclaimer ? <sup>†</sup> : ''}</span>
          </div>
          <div className="px-2 py-4 md:py-5 md:px-5 flex flex-col justify-center md:justify-start items-center md:items-start bg-sky-100">
            <span className="text-lg md:text-2xl font-semibold">{!isNil(get(selectedOffer, 'term')) ? `${get(selectedOffer, 'term')} mo` : '-'}</span>
            <span className="text-center md:text-left uppercase text-xs text-slate-500">term</span>
          </div>
          <div className="px-2 py-4 md:py-5 md:px-5 flex flex-col justify-center md:justify-start items-center md:items-start bg-sky-100">
            <span className="text-lg md:text-2xl font-semibold">{!isNil(get(selectedOffer, 'monthlyPaymentAmount')) ? `$${formatCurrency(get(selectedOffer, 'monthlyPaymentAmount'))}` : '-'}</span>
            <span className="text-center md:text-left uppercase text-xs text-slate-500">est. monthly payment</span>
          </div>
          <div className="px-2 py-4 md:py-5 md:px-5 flex flex-col justify-center md:justify-start items-center md:items-start bg-sky-100">
            <span className="text-lg md:text-2xl font-semibold">{!isNil(get(selectedOffer, 'finalPaymentAmount')) ? `$${formatCurrency(get(selectedOffer, 'finalPaymentAmount'))}` : '-'}</span>
            <span className="text-center md:text-left uppercase text-xs text-slate-500">est. balloon payment</span>
          </div>
        </div>
      </div>

      <FormRow className="w-full">
        <FormColumn>
          <span className="text-xs md:text-sm text-slate-500 text-center w-full block">Balloon payment is due at the end of the term</span>
        </FormColumn>
      </FormRow>

      <GeneralError error={generalError} className="w-full" />

      <FormRow className="w-full">
        <Button
          className="w-full"
          label="Next Step: Funding Account"
          onClick={handleNext}
          isLoading={isSubmitting}
        />
      </FormRow>

      {hasAnyOtherOffers ? (
        <>
          <hr className="w-full my-4" />

          <div className="w-full mt-4">
            <div className="flex flex-col gap-4">
              <h5 className="text-base font-semibold">Other Available Options</h5>
              <div className="flex gap-1 bg-slate-100 p-1 rounded-lg self-start w-full">
                {tabsWithOtherOffers.map(({ tab, repaymentType, hasOffers }) => (
                  hasOffers ? (
                    <button
                      key={tab.value}
                      onClick={() => setActiveTab(repaymentType)}
                      className={`flex-1 leading-[1.2] px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                        activeTab === repaymentType
                          ? 'bg-blue-500 text-white shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ) : (
                    <InfoTooltip
                      key={tab.value}
                      className="flex-1 [&>button]:w-full"
                      position="bottom"
                      tooltip={`There are no other ${tab.label} options available.`}
                      mobileTooltip={`There are no other ${tab.label} options available.`}
                    >
                      <span className="w-full block text-center leading-[1.2] px-4 py-3 text-sm font-medium rounded-lg text-slate-400 opacity-50 cursor-not-allowed">
                        {tab.label}
                      </span>
                    </InfoTooltip>
                  )
                ))}
              </div>
            </div>
            <div className="mt-4 flex flex-col gap-4">
              {otherOffers.map((offer, index) => (
                <div
                  onClick={(e) => handleOfferSelection(e, offer, index)}
                  className="relative py-3 px-4 rounded bg-slate-50 border border-gray-100 transition-colors hover:border-blue-400 hover:bg-blue-50 cursor-pointer"
                  key={index}
                >
                  {offerBeingSelected === index ? (
                    <div className="w-full absolute h-full top-0 left-0 rounded z-30 bg-white bg-opacity-75 flex items-center justify-center">
                      <Spinner color="blue-400" />
                    </div>
                  ) : null}
                  <div className="flex gap-2 items-center text-blue-400 mb-1">
                    <div className="text-base font-bold leading-6">
                      $
                      {!isNil(get(offer, 'loanAmount')) ? formatCurrency(get(offer, 'loanAmount')) : '-'}
                      <span className="text-sm font-normal"> Loan Amount</span>
                    </div>
                  </div>
                  <div className="flex justify-between gap-6 mb-1 items-center">
                    <div className="text-base font-bold leading-6">
                      {activeTab === 'No Payment' ? (
                        <>No monthly payments</>
                      ) : (
                        <>
                        $
                        {!isNil(get(offer, 'monthlyPaymentAmount')) ? formatCurrency(get(offer, 'monthlyPaymentAmount')) : '-'}
                        <span className="text-sm font-normal">/mo</span>
                          </>
                        )}
                    </div>
                    <div className="flex gap-2 items-center">
                      {get(offer, 'coin') ? getCryptoIcon(get(offer, 'coin').toLowerCase()) : null}
                      <span className="text-sm font-semibold leading-6">
                        {get(offer, 'collateralRequired') || '-'}
                        {' '}
                        {get(offer, 'coin') ? get(offer, 'coin').toUpperCase() : null}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between mb-1 gap-6">
                    <span className="text-sm leading-6">
                      $
                      {get(offer, 'finalPaymentAmount') ? formatCurrency(get(offer, 'finalPaymentAmount')) : '-'}
                      {' '}
                      Balloon Payment
                    </span>
                    <span className="text-sm font-normal text-blue-400 leading-6">
                      {get(offer, 'ltv') ? formatPercentage(get(offer, 'ltv')) : '-'}
                      % LTV
                    </span>
                  </div>
                  <div className="text-sm font-normal leading-6">
                    {get(offer, 'apr') ? formatPercentage(get(offer, 'apr')) : '-'}
                    % APR ·
                    {' '}
                    {get(offer, 'term') || '-'}
                    {' '}
                    months
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}

      <hr className="w-full my-6" />

      <div className="w-full border rounded border-blue-400 bg-sky-100 p-6">
        <h5 className="font-semibold text-xl mb-6">Need to Adjust Your Loan?</h5>
        <div className="border bg-white border-blue-300 rounded-lg p-4">
          <div onClick={handleAdjustSectionClick} className="flex justify-between items-center cursor-pointer">
            <span className="font-medium text-sm text-slate-600">Change Loan Options</span>
            {isAdjustExpanded ? (
              <ChevronUp className="h-4 w-4 text-slate-600" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-600" />
            )}
          </div>
          {isAdjustExpanded && (
            <>
              <FormRow className="mt-4">
                <FormColumn>
                  <Input
                    placeholder="Loan Amount"
                    mask={currencyMask}
                    value={values.requestedAmount}
                    onChange={(e) => handleChange('requestedAmount', e.target.value)}
                    required
                    error={errors.requestedAmount}
                  />
                </FormColumn>
              </FormRow>
              {regradeErrorMessage ? (
                <FormRow>
                  <FormColumn>
                    <Alert variant="danger">
                      <p className='text-sm'>
                        {regradeErrorMessage}
                      </p>
                    </Alert>
                  </FormColumn>
                </FormRow>
              ) : null}
              <FormRow className="mb-0">
                <FormColumn>
                  <Button
                    className="w-full text-sm !py-3 !px-4"
                    label="Update Loan"
                    onClick={handleRegrade}
                    isLoading={isRegrading}
                  />
                </FormColumn>
              </FormRow>
            </>
          )}
        </div>
      </div>

      {isCongratulationsModalShown ? (
        <Modal onClose={toggleCongratulationsModal}>
          <div className="text-center -mt-8">
            <img className="w-40 h-40 mx-auto" src={CongratulationsImg} />
            <h4 className="font-semibold">Congrats {get(workflow, 'data.firstName')}!</h4>
            <h5 className="text-2xl font-semibold text-blue-500 mb-2">{isCounterOffer ? 'Your loan offer is ready.' : "You're Pre-approved"}</h5>
            {isCounterOffer ? (
              <p className="text-sm text-slate-500 w-initial sm:w-[280px] mx-auto mb-8">
                While we couldn't approve the full amount requested, we do have {totalOfferCount > 1 ? 'other great offers' : 'another great offer'} available for you.
              </p>
            ) : (
              <p className="text-sm text-slate-500 w-initial sm:w-[280px] mx-auto mb-8">
                <span className="font-semibold">Your loan offer is ready.</span> Please review the details on this screen and confirm your offer to move forward.
              </p>
            )}
          </div>
        </Modal>
      ) : null}
    </div>
  );
};

SelectOffer.propTypes = {
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
  validator: PropTypes.object.isRequired,
};

export default Validator(RegradeSchema)(SelectOffer);
